import { Navigate } from "react-router-dom"
import type { ReactNode } from "react"
import { useAuthStore, type Role } from "@/stores/auth"
import { hasAuthSessionHint } from "@/lib/bootstrap-auth"
import { PageLoader } from "@/components/ui/loading"

interface ProtectedRouteProps {
  children: ReactNode
  allowedRoles?: Role[]
  redirectTo?: string
}

/* S6 5b/A5: identity comes from the synchronous localStorage seed (dual-mode
 * fast path) OR the async GET /auth/me bootstrap (cookie-only sessions). Presence
 * is checked on user/role, not the token (which is null under httpOnly cookies).
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo = "/login",
}: ProtectedRouteProps) {
  const role = useAuthStore((s) => s.role)
  const user = useAuthStore((s) => s.user)
  const bootstrapped = useAuthStore((s) => s.bootstrapped)

  if (!user || !role) {
    // No identity in the store yet. If a session is HINTED (auth_status cookie)
    // and the /me bootstrap hasn't finished, WAIT with a loader instead of
    // redirecting — this prevents the flash-to-login for a cookie-only session
    // on hard refresh (the regression the old synchronous seed avoided).
    if (!bootstrapped && hasAuthSessionHint()) return <PageLoader />
    return <Navigate to={redirectTo} replace />
  }
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={redirectTo} replace />
  }
  return <>{children}</>
}
